import { cn } from '@/lib/utils'

interface MetricCardProps {
  label: string
  value: number | string
  unit?: string
  variant?: 'default' | 'success' | 'warning' | 'info'
  size?: 'sm' | 'md' | 'lg'
}

export function MetricCard({
  label,
  value,
  unit,
  variant = 'default',
  size = 'md',
}: MetricCardProps) {
  // Format numbers nicely
  const formattedValue =
    typeof value === 'number'
      ? value < 0.01 && value > 0
        ? value.toExponential(3)
        : value.toLocaleString(undefined, { maximumFractionDigits: 4 })
      : value

  // Humanize the label (snake_case/camelCase to Title Case)
  const humanizedLabel = label
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim()

  const variantStyles = {
    default: 'bg-muted/30 border-border',
    success: 'bg-success-muted/50 border-success/30',
    warning: 'bg-warning-muted/50 border-warning/30',
    info: 'bg-info-muted/50 border-info/30',
  }

  const sizeStyles = {
    sm: 'px-2 py-1',
    md: 'px-3 py-2',
    lg: 'px-4 py-3',
  }

  const valueSizeStyles = {
    sm: 'text-sm',
    md: 'text-lg',
    lg: 'text-2xl',
  }

  return (
    <div
      className={cn(
        'rounded-lg border inline-flex flex-col',
        variantStyles[variant],
        sizeStyles[size],
      )}
    >
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
        {humanizedLabel}
      </span>
      <div className="flex items-baseline gap-1">
        <span className={cn('font-semibold', valueSizeStyles[size])}>{formattedValue}</span>
        {unit && <span className="text-xs text-muted-foreground">{unit}</span>}
      </div>
    </div>
  )
}
